<template>
  <div
    class="app"
    :class="{
      'app_without-layout': !showAppLayout,
    }"
  >
    <AppLoadingBar :is-loading="isLoading" />
    <AppHeader
      v-if="showAppLayout"
      class="app__header"
      :data-theme="frameColorTheme"
    >
      <template #logo>
        <slot name="logo" />
      </template>
      <template #title>
        <slot name="title" />
      </template>
      <template #actions>
        <slot name="actions" />
      </template>
      <template
        #sidebar="{
          close, isVisible,
        }"
      >
        <slot
          name="sidebar"
          :close="close"
          :is-visible="isVisible"
        />
      </template>
    </AppHeader>
    <AppNavbar
      v-if="showAppLayout"
      class="app__navbar"
      :data-theme="frameColorTheme"
      :show-sidebar="showSidebar"
    >
      <template #default>
        <slot name="navbar" />
      </template>
    </AppNavbar>
    <slot
      class="app__message"
      name="message"
    />
    <main
      class="app__main"
      :data-keyboard-area="EKeyboardArea.PageContent"
    >
      <slot v-if="isContentVisible">
        <router-view />
      </slot>
    </main>
  </div>
</template>

<script setup lang="ts">
import {
  shallowRef,
} from 'vue';

import {
  useKeyboardAreas,
} from '../../composable/use-keyboard-areas.ts';
import {
  useTableKeyboardNavigation,
} from '../../composable/use-table-keyboard-navigation.ts';
import {
  EKeyboardArea,
} from '../../utils/keyboard-area.ts';
import AppLoadingBar from '../app-loading-bar/app-loading-bar.vue';
import AppNavbar from '../app-navbar/app-navbar.vue';

import AppHeader from './app-header.vue';

const showSidebar = shallowRef(false);

useKeyboardAreas();
useTableKeyboardNavigation();

withDefaults(
  defineProps<{
    showAppLayout?: boolean,
    isLoading?: boolean,
    isContentVisible?: boolean,
    /** The color theme of the top bar and the navigation rail, when the active theme gives them their own. */
    frameColorTheme?: string,
  }>(), {
    showAppLayout: true,
    isLoading: false,
    isContentVisible: true,
    frameColorTheme: undefined,
  });

defineSlots<{
  default?(): any,
  logo(): any,
  title(): any,
  actions(): any,
  navbar(): any,
  sidebar?(props: {
    isVisible: boolean,
    close: () => void,
  }): any,
  message?(): any,
}>();
</script>

<style scoped lang="scss">
@use '../../styles/mixins/media';

.app {
  width: 100dvw;
  height: 100dvh;
  display: grid;
  grid-template: "header header" auto "navbar message" auto "navbar main" 1fr / auto 1fr;

  &__header {
    display: flex;
    flex-direction: row;
    gap: var(--gap);
    align-items: center;
    position: sticky;
    top: 0;
    grid-area: header;
    padding: calc(.5rem + env(safe-area-inset-top)) 16px .5rem calc(.5rem + env(safe-area-inset-left));
    border-bottom: 1px solid var(--border-color-frame);
  }

  &__message {
    grid-area: message;
  }

  &__navbar {
    grid-area: navbar;
    max-height: calc(100dvh - var(--navbar-header-height, 0));
    position: sticky;
    top: var(--navbar-header-height, 0);

    @include media.media(mobile) {
      display: none;
      width: 0;
      overflow: none;
    }
  }

  &__main {
    position: relative;
    grid-area: main;
    overflow: auto;
  }

  // Pages without the app layout, such as the playground, scroll the document instead of the main
  // area, so that a full size screenshot of the browser captures all of their content.
  &_without-layout {
    width: auto;
    height: auto;
    min-height: 100dvh;
  }

  &_without-layout &__main {
    overflow: visible;
  }
}
</style>
